import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HeaderProps } from '../../types';
import { colors, typography, layout } from '../../styles/designTokens';

const Header: React.FC<HeaderProps> = ({
  logoImage,
  navLinks,
  isMobileMenuOpen: externalIsMobileMenuOpen,
  onMobileMenuToggle: externalOnMobileMenuToggle,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [internalIsMobileMenuOpen, setInternalIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);

  // שימוש ב-state פנימי אם לא סופק חיצוני
  const isMobileMenuOpen = externalIsMobileMenuOpen ?? internalIsMobileMenuOpen;
  const onMobileMenuToggle = React.useMemo(
    () => externalOnMobileMenuToggle ?? (() => {
      setInternalIsMobileMenuOpen(prev => !prev);
    }),
    [externalOnMobileMenuToggle]
  );

  // Keyboard handler - Escape לסגירת תפריט נייד
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        onMobileMenuToggle();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen, onMobileMenuToggle]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const sectionId = href.substring(2);
      
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      setIsScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* Top Border - קו דק בראש - עם צבע כמו רקע יצירת קשר - חלק מהאידר הקבוע */}
      <div 
        className="h-[3px] w-full" 
        style={{ 
          background: colors.accentSpecial.buttonGradientDiagonal,
          position: 'fixed', // fixed יחד עם האידר
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 10000, // גבוה מהאידר כדי שיהיה מעליו
        }}
      ></div>

      {/* Header Section - מדויק כמו ב-Figma - עם fixed כדי שיישאר תמיד נראה */}
      <header 
        className={`header-main shadow-lg flex items-center ${isScrolled ? 'scrolled' : ''}`}
          style={{ 
            position: 'fixed',
            top: '3px',
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 9999,
            background: isScrolled 
              ? `linear-gradient(135deg, rgba(184, 130, 46, 0.95) 0%, rgba(212, 148, 58, 0.95) 20%, rgba(232, 168, 79, 0.95) 40%, rgba(245, 182, 88, 0.95) 60%, rgba(252, 200, 122, 0.95) 80%, rgba(255, 216, 153, 0.95) 100%)` 
              : colors.accentSpecial.buttonGradientDiagonal,
            backdropFilter: isScrolled ? 'blur(12px) saturate(180%)' : 'none',
            WebkitBackdropFilter: isScrolled ? 'blur(12px) saturate(180%)' : 'none',
            transition: 'background 0.3s ease, backdrop-filter 0.3s ease, -webkit-backdrop-filter 0.3s ease, border-bottom 0.3s ease',
            overflow: 'visible',
            maxWidth: '100%',
            borderBottom: '1px solid #000000',
            height: 'clamp(50px, 6vw, 60px)',
            minHeight: '50px',
          }}
      >
        <div className="container mx-auto px-4 w-full" style={{ maxWidth: layout.contentMaxWidth }}>
          <div className="flex items-center justify-between w-full gap-4">
            {/* Left Side - Quick Purchase Button - כפתור רכישה מהירה */}
            <div className="flex items-center flex-shrink-0 order-3" style={{ marginRight: 'clamp(4px, 1vw, 16px)' }}>
              <a
                href="/payment"
                className="quick-purchase-btn group whitespace-nowrap font-bold rounded-full"
                aria-label="לחץ למעבר לעמוד רכישה מהירה של מכונת תספורת AiRY"
                style={{
                  backgroundColor: '#000000',
                  color: '#F5B658',
                  fontSize: 'clamp(12px, 1.8vw, 18px)', // מובייל: 12px, דסקטופ: 18px
                  letterSpacing: typography.body.letterSpacing,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  padding: 'clamp(6px, 1.2vw, 11px) clamp(12px, 2.5vw, 24px) clamp(6px, 1.2vw, 11px) clamp(38px, 5.5vw, 58px)', // מובייל: padding קטן יותר
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  position: 'relative',
                  // animation: 'buttonColorSwitch 2s steps(1, end) infinite',
                  animation: 'buttonColorSwitch 3s steps(1, end) infinite',

                }}
              >
                <span>לרכישה מהירה</span>
                
                {/* עיגול לבן עם חץ כתום - position absolute */}
                <div 
                  className="flex items-center justify-center rounded-full bg-black flex-shrink-0 transition-all duration-75 ease-out group-hover:-translate-x-2"
                  style={{
                    position: 'absolute',
                    left: '0px',
                    width: 'clamp(32px, 4.8vw, 48px)', // מובייל: 32px, דסקטופ: 48px
                    height: 'clamp(32px, 4.8vw, 48px)',
                    border: '2px solid #D4943A',
                  }}
                >
                  {/* אייקון חץ */}
                  <svg 
                    className="transition-transform duration-75 ease-out rotate-45 group-hover:!rotate-0"
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    style={{
                      color: colors.gold.base,
                    }}
                  >
                    <path 
                      d="M19 12H5M5 12L12 5M5 12L12 19" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </a>
            </div>
            
            {/* Logo - משתמש בלוגו מ-assets - Responsive - ללא אפקטים */}
            <div className="header-logo-container flex items-center flex-shrink-0 order-1">
              <a href="/" aria-label="חזרה לדף הבית">
              <img 
                src={logoImage} 
                alt="לוגו איירי - מכונת תספורת חדשנית עם מערכת שאיבה מובנית" 
                width="2480"
                height="1346"
                className="header-logo h-10 md:h-14 w-auto"
                style={{ 
                  maxHeight: 'clamp(28px, 5.5vw, 42px)', // מובייל: 28px, דסקטופ: 42px
                  height: 'clamp(28px, 5.5vw, 42px)',
                  width: 'auto',
                  objectFit: 'contain',
                }}
                loading="eager"
              />
              </a>
            </div>
            
            {/* Center - Navigation Links - מדויק כמו ב-Figma - עם hover animations */}
            <nav className="header-nav hidden lg:flex items-center flex-1 justify-center order-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="header-nav-link transition-all duration-300 whitespace-nowrap relative group"
                  aria-label={`מעבר ל${link.label}`}
                  style={{ 
                    color: '#000000',
                    letterSpacing: typography.body.letterSpacing,
                    fontWeight: 'bold',
                    fontSize: 'clamp(16px, 2vw, 20px)',
                    marginLeft: 'clamp(12px, 1.5vw, 20px)',
                    marginRight: 'clamp(12px, 1.5vw, 20px)',
                    padding: '6px 12px',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderRadius = '6px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              ))}
            </nav>
              
            {/* Mobile Menu Button - Responsive */}
            <button 
              className="lg:hidden flex flex-col justify-center items-center p-2 ml-4 bg-transparent order-4"
              onClick={onMobileMenuToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onMobileMenuToggle();
                }
              }}
              aria-label={isMobileMenuOpen ? 'סגור תפריט ניווט' : 'פתח תפריט ניווט'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              style={{
                position: 'relative',
                zIndex: 10001,
                cursor: 'pointer',
                width: '40px',
                height: '40px',
              }}
            >
              <div style={{ position: 'relative', width: '32px', height: '20px' }}>
                <span 
                  style={{ 
                    position: 'absolute',
                    width: '32px',
                    height: '2px',
                    backgroundColor: '#000000',
                    top: isMobileMenuOpen ? '9px' : '0',
                    transform: isMobileMenuOpen ? 'rotate(45deg)' : 'rotate(0)',
                    transition: 'all 0.3s ease',
                  }}
                  aria-hidden="true"
                ></span>
                <span 
                  style={{ 
                    position: 'absolute',
                    width: '32px',
                    height: '2px',
                    backgroundColor: '#000000',
                    top: '9px',
                    opacity: isMobileMenuOpen ? 0 : 1,
                    transition: 'all 0.3s ease',
                  }}
                  aria-hidden="true"
                ></span>
                <span 
                  style={{ 
                    position: 'absolute',
                    width: '32px',
                    height: '2px',
                    backgroundColor: '#000000',
                    top: isMobileMenuOpen ? '9px' : '18px',
                    transform: isMobileMenuOpen ? 'rotate(-45deg)' : 'rotate(0)',
                    transition: 'all 0.3s ease',
                  }}
                  aria-hidden="true"
                ></span>
              </div>
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation Menu - בפינה שמאלית */}
        {isMobileMenuOpen && (
          <nav 
            ref={mobileMenuRef}
            id="mobile-menu"
            role="navigation"
            aria-label="תפריט ניווט נייד"
            className="lg:hidden absolute pb-4 pt-4"
            style={{ 
              background: colors.accentSpecial.buttonGradientDiagonal,
              borderTop: `2px solid rgba(212, 148, 58, 0.8)`,
              boxShadow: '0 4px 12px rgba(212, 148, 58, 0.3)',
              top: '100%',
              left: 0,
              zIndex: 9998,
              width: '200px',
              borderRadius: '0 0 8px 0',
            }}
          >
            <div className="flex flex-col space-y-4 px-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-base transition-colors duration-300 py-2 text-right font-normal"
                  aria-label={`מעבר ל${link.label}`}
                  style={{ 
                    color: '#000000',
                    letterSpacing: typography.body.letterSpacing,
                    fontWeight: link.isActive ? 'bold' : 'normal',
                  }}
                  onClick={(e) => {
                    handleNavClick(e, link.href);
                    onMobileMenuToggle();
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </nav>
        )}
      </header>
    </>
  );
};

export default Header;

